import React, { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { Button, Col, Input, Row, Tabs } from 'antd';
import { Link, useHistory } from 'react-router-dom';
import debounce from 'lodash.debounce';
import { objectToQuery } from 'common/helpers/filters.helper';
import { CommonLayout } from 'common/components/Layouts/CommonLayout';
import { ELayoutSize } from 'common/const/common.const';
import { ERoutesPrivate } from 'common/models/routesModel';
import { useQueryFilter } from 'common/hooks/useQueryFilter';
import { EntityStatusSelector } from 'common/components/EntityStatusSelector';
import { AssetTable } from 'entities/Asset/components/AssetTable';
import { assetTableConfig } from 'entities/Asset/Asset.models';
import { CategoryTable } from 'entities/Category/components/CategoryTable';
import { categoryTableConfig } from 'entities/Category/Category.models';
import { MenuItemTable } from 'entities/MenuItem/components/MenuItemTable';
import { menuItemTableConfig } from 'entities/MenuItem/MenuItem.models';
import { ECommonEntityStatus } from 'entities/Bitecoin/Bitecoin.models';

export enum EMenuPageTabKeys {
  Categories = 'categories',
  Items = 'items',
  Assets = 'assets',
}
enum EMenuPageTabName {
  Categories = 'Categories',
  Items = 'Items',
  Assets = 'Assets',
}

export interface IMenuPageState {
  tab?: EMenuPageTabKeys;
  pageIndex?: number;
  statuses?: ECommonEntityStatus[];
  name?: string;
}

const DEFAULT_FILTER = {
  tab: EMenuPageTabKeys.Categories,
  pageIndex: 1,
  statuses: [ECommonEntityStatus.Active],
  name: undefined,
};

export const MenuPageComponent: React.FC = () => {
  const history = useHistory();
  const [tab, setTab] = useState<EMenuPageTabKeys | undefined>(undefined);
  const { filter, updateFilter } = useQueryFilter(DEFAULT_FILTER);

  const selectTab = (parsedQueryTab?: string) => {
    switch (parsedQueryTab) {
      case EMenuPageTabKeys.Categories:
        return EMenuPageTabKeys.Categories;

      case EMenuPageTabKeys.Items:
        return EMenuPageTabKeys.Items;

      case EMenuPageTabKeys.Assets:
        return EMenuPageTabKeys.Assets;

      default:
        return EMenuPageTabKeys.Categories;
    }
  };

  const onTabChange = (tab?: string, isInitial?: boolean) => {
    const newTab = selectTab(tab);
    const newFilter: IMenuPageState = { tab: newTab };
    
    setTab(newTab);

    if (!filter.statuses) {
      newFilter.statuses = [ECommonEntityStatus.Active];
    }

    if (isInitial) {
      updateFilter({ ...newFilter, ...filter });

      history.replace({ search: objectToQuery({ ...newFilter, ...filter }) });

      return;
    }

    updateFilter({
      ...DEFAULT_FILTER,
      ...newFilter,
    });

    history.replace({
      search: objectToQuery(newFilter),
    });
  };

  useEffect(() => {
    onTabChange(filter.tab, true);
  }, []);

  const autocompleteSearch = useCallback(
    debounce((text: string) => {
      updateFilter({ name: text === '' ? undefined : text });
    }, 300),
    [updateFilter]
  );

  const onChangeStatus = (status: ECommonEntityStatus[] | undefined) => updateFilter({ statuses: status });

  return (
    <CommonLayout typeLayout={ELayoutSize.Large} classStyle="mt-10">
      <Tabs onChange={onTabChange} type="card" activeKey={tab}>
        <Tabs.TabPane tab={EMenuPageTabName.Categories} key={EMenuPageTabKeys.Categories}>
          {tab === EMenuPageTabKeys.Categories && (
            <>
              <Row className="mb-10 width-full" justify="space-between" align="bottom">
                <Col span={20}>
                  <Row justify="start" align="bottom">
                    <Col span={8} className="mr-4">
                      <span className="mb-3">Search by name</span>
                      <Input.Search
                        key={tab}
                        placeholder="Search by name"
                        defaultValue={filter.name}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => autocompleteSearch(e.target.value)}
                        className="mt-3"
                      />
                    </Col>
                    <Col span={8}>
                      <EntityStatusSelector onChangeStatus={onChangeStatus} value={filter.statuses} />
                    </Col>
                  </Row>
                </Col>
                <Col span={4}>
                  <Link to={ERoutesPrivate.CategoryCreate}>
                    <Button block type="primary" title="Add Category">
                      Add Category
                    </Button>
                  </Link>
                </Col>
              </Row>
              <Col span={16}>
                <CategoryTable config={categoryTableConfig} filter={filter} />
              </Col>
            </>
          )}
        </Tabs.TabPane>
        <Tabs.TabPane tab={EMenuPageTabName.Items} key={EMenuPageTabKeys.Items}>
          {tab === EMenuPageTabKeys.Items && (
            <>
              <Row className="mb-10 width-full" justify="space-between" align="bottom">
                <Col span={20}>
                  <Row justify="start" align="bottom">
                    <Col span={8} className="mr-4">
                      <span className="mb-3">Search by name or category</span>
                      <Input.Search
                        key={tab}
                        placeholder="Search by name or category"
                        defaultValue={filter.name}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => autocompleteSearch(e.target.value)}
                        className="mt-3"
                      />
                    </Col>
                    <Col span={8}>
                      <EntityStatusSelector onChangeStatus={onChangeStatus} value={filter.statuses} />
                    </Col>
                  </Row>
                </Col>
                <Col span={4}>
                  <Link to={ERoutesPrivate.MenuItemCreate}>
                    <Button type="primary" title="New item">
                      New item
                    </Button>
                  </Link>
                </Col>
              </Row>
              <Col span={24}>
                <MenuItemTable config={menuItemTableConfig} filter={filter} />
              </Col>
            </>
          )}
        </Tabs.TabPane>
        <Tabs.TabPane tab={EMenuPageTabName.Assets} key={EMenuPageTabKeys.Assets}>
          {tab === EMenuPageTabKeys.Assets && (
            <>
              <Row className="mb-10 width-full" justify="space-between" align="bottom">
                <Col span={20}>
                  <Row justify="start" align="bottom">
                    <Col span={8} className="mr-4">
                      <span className="mb-3">Search by name</span>
                      <Input.Search
                        key={tab}
                        placeholder="Search by name"
                        defaultValue={filter.name}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => autocompleteSearch(e.target.value)}
                        className="mt-3"
                      />
                    </Col>
                  </Row>
                </Col>
                <Col span={4}>
                  <Link to={ERoutesPrivate.AssetCreate}>
                    <Button type="primary" title="New asset">
                      New asset
                    </Button>
                  </Link>
                </Col>
              </Row>

              <Col span={12}>
                <AssetTable config={assetTableConfig} filter={filter} />
              </Col>
            </>
          )}
        </Tabs.TabPane>
      </Tabs>
    </CommonLayout>
  );
};

export const MenuPage = MenuPageComponent;
